
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { startAdminRealtimeUiBridge } from './adminRealtimeUiBridge';

const AdminRealtimePanelContext = createContext(null);

/*
 * v12: один master-switch и один runtime для realtime UI всей админки.
 *
 * Специализированные hooks страниц (например useCatalogReviewSocket) могут
 * оставаться транспортом доменных данных/операций, но курсор, hover, scroll,
 * camera и универсальные field-locks принадлежат только
 * adminRealtimeUiBridge.
 *
 * При добавлении новой страницы больше не создаём второй cursor renderer или
 * второй socket-manager. Добавляется ОДИН PAGE_CONFIG (route + root + policy),
 * а обычные кнопки/инпуты определяются generic bridge автоматически. Только
 * сложное React-состояние при необходимости получает semantic state namespace.
 * Метаданные страницы публикуются через useAdminRealtimePanelPublisher().
 */
const ADMIN_REALTIME_STORAGE_KEY = 'adminRealtime:enabled';

const DEFAULT_PAGE_SOURCES = {
    'catalog-review': {
        title: 'Совместная работа с каталогом',
        roomLabel: 'Каталог форм и цен фрезеровки',
        pageLabel: 'Каталог',
        focusLabel: 'Общий интерфейс',
    },
    'product-seo-price': {
        title: 'Цены, работы и 3D',
        roomLabel: 'Общий экран цен, работ, заготовок и 3D',
        pageLabel: 'Цены, работы и теги',
        focusLabel: 'Полное зеркало интерфейса',
    },
};

const readRealtimeEnabled = () => {
    if (typeof window === 'undefined') return true;

    try {
        const current = window.localStorage.getItem(ADMIN_REALTIME_STORAGE_KEY);
        if (current !== null) return current !== '0';

        // Мягкая миграция со старого ключа product-seo.
        const legacyProductSeo = window.localStorage.getItem(
            'adminRealtimeUiBridge:productSeoEnabled'
        );
        if (legacyProductSeo !== null) return legacyProductSeo !== '0';

        return true;
    } catch (_error) {
        return true;
    }
};

const getUserActivityTime = (user = {}) => {
    const raw = user.lastSeenAt || user.joinedAt || 0;
    const numeric = Number(raw);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;

    const parsed = Date.parse(String(raw || ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

/*
 * Сервер хранит presence по socketId — это нужно для нескольких вкладок и
 * устройств. UI показывает людей, поэтому одинаковый userId объединяется в
 * одного участника. connectionCount сохраняем для диагностики.
 */
const normalizeUsers = (users = []) => {
    const uniqueUsers = new Map();

    (Array.isArray(users) ? users : [])
        .filter(Boolean)
        .map((user) => ({
            socketId: String(user.socketId || user.id || ''),
            userId: String(user.userId || user.id || ''),
            name: String(user.name || 'Администратор'),
            role: user.role || null,
            focusType: user.focusType || null,
            focusNumber: user.focusNumber ?? null,
            focusKey: String(user.focusKey || ''),
            focusLabel: String(user.focusLabel || ''),
            joinedAt: user.joinedAt || null,
            lastSeenAt: user.lastSeenAt || null,
        }))
        .forEach((user) => {
            const identityKey = user.userId
                ? `user:${user.userId}`
                : `socket:${user.socketId}`;
            const previous = uniqueUsers.get(identityKey);

            if (!previous) {
                uniqueUsers.set(identityKey, {
                    ...user,
                    connectionCount: 1,
                });
                return;
            }

            const useIncoming = getUserActivityTime(user) >= getUserActivityTime(previous);
            const preferred = useIncoming ? user : previous;

            uniqueUsers.set(identityKey, {
                ...preferred,
                connectionCount: Number(previous.connectionCount || 1) + 1,
            });
        });

    return Array.from(uniqueUsers.values());
};

const normalizeMetadata = (sourceId, payload = {}) => ({
    sourceId: String(sourceId || ''),
    title: String(payload.title || 'Совместная работа'),
    roomLabel: String(payload.roomLabel || 'Активная комната'),
    pageLabel: String(payload.pageLabel || ''),
    categoryLabel: String(payload.categoryLabel || ''),
    focusType: String(payload.focusType || ''),
    focusNumber: payload.focusNumber ?? null,
    focusKey: String(payload.focusKey || ''),
    focusLabel: String(payload.focusLabel || ''),
    operationsCount: Math.max(0, Number(payload.operationsCount || 0)),
});

export const AdminRealtimePanelProvider = ({ children }) => {
    const [publishedSource, setPublishedSource] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [realtimeEnabled, setRealtimeEnabledState] = useState(readRealtimeEnabled);
    const [uiBridgeStatus, setUiBridgeStatus] = useState({
        pageKey: '',
        connected: false,
        users: [],
        socketId: '',
        lockedFieldsCount: 0,
    });

    /*
     * Оставляем legacy callback registry только для совместимости со старыми
     * page publishers. Источник истины enabled теперь всё равно один.
     */
    const sourceActionsRef = useRef(new Map());
    const realtimeEnabledRef = useRef(realtimeEnabled);
    const publishedSourceRef = useRef(publishedSource);

    useEffect(() => {
        realtimeEnabledRef.current = realtimeEnabled;
    }, [realtimeEnabled]);

    useEffect(() => {
        publishedSourceRef.current = publishedSource;
    }, [publishedSource]);

    const setRealtimeEnabled = useCallback((enabled) => {
        const nextEnabled = enabled === true;
        realtimeEnabledRef.current = nextEnabled;
        setRealtimeEnabledState(nextEnabled);

        try {
            window.localStorage.setItem(
                ADMIN_REALTIME_STORAGE_KEY,
                nextEnabled ? '1' : '0'
            );
        } catch (_error) {
            // localStorage может быть недоступен.
        }
    }, []);

    const publishSource = useCallback((sourceId, payload = {}) => {
        if (!sourceId) return;

        const normalizedSourceId = String(sourceId);
        const onEnabledChange = typeof payload.onEnabledChange === 'function'
            ? payload.onEnabledChange
            : null;

        if (onEnabledChange) {
            sourceActionsRef.current.set(normalizedSourceId, { onEnabledChange });
        } else {
            sourceActionsRef.current.delete(normalizedSourceId);
        }

        const next = normalizeMetadata(normalizedSourceId, payload);
        publishedSourceRef.current = next;

        setPublishedSource((previous) => {
            if (
                previous
                && previous.sourceId === next.sourceId
                && JSON.stringify(previous) === JSON.stringify(next)
            ) {
                return previous;
            }

            return next;
        });
    }, []);

    const clearSource = useCallback((sourceId) => {
        const normalizedSourceId = String(sourceId || '');
        sourceActionsRef.current.delete(normalizedSourceId);

        setPublishedSource((previous) => {
            if (previous?.sourceId !== normalizedSourceId) return previous;
            publishedSourceRef.current = null;
            return null;
        });
    }, []);

    /*
     * Bridge один на всё приложение и монтируется в App ровно один раз через
     * этот Provider. Page components больше не создают собственный UI cursor
     * transport.
     */
    const isUiBridgeEnabled = useCallback(() => (
        realtimeEnabledRef.current
    ), []);

    const getPresenceMeta = useCallback((pageKey) => {
        const current = publishedSourceRef.current;
        if (!current || current.sourceId !== pageKey) return {};

        return {
            focusType: current.focusType || '',
            focusNumber: current.focusNumber ?? null,
            focusKey: current.focusKey || '',
            focusLabel: current.focusLabel || '',
        };
    }, []);

    useEffect(() => startAdminRealtimeUiBridge({
        getEnabled: isUiBridgeEnabled,
        getPresenceMeta,
        onStatusChange: setUiBridgeStatus,
    }), [getPresenceMeta, isUiBridgeEnabled]);

    const pageKey = String(
        uiBridgeStatus.pageKey
        || publishedSource?.sourceId
        || ''
    );

    const source = useMemo(() => {
        if (!pageKey) return null;

        const defaultMetadata = DEFAULT_PAGE_SOURCES[pageKey] || null;
        const pageMetadata = publishedSource?.sourceId === pageKey
            ? publishedSource
            : null;
        const metadata = pageMetadata || (
            defaultMetadata
                ? normalizeMetadata(pageKey, defaultMetadata)
                : null
        );

        if (!metadata) return null;

        return {
            ...metadata,
            enabled: realtimeEnabled,
            toggleAvailable: true,
            connected: realtimeEnabled && uiBridgeStatus.connected === true,
            users: realtimeEnabled
                ? normalizeUsers(uiBridgeStatus.users)
                : [],
            lockedFieldsCount: realtimeEnabled
                ? Math.max(0, Number(uiBridgeStatus.lockedFieldsCount || 0))
                : 0,
        };
    }, [
        pageKey,
        publishedSource,
        realtimeEnabled,
        uiBridgeStatus.connected,
        uiBridgeStatus.lockedFieldsCount,
        uiBridgeStatus.users,
    ]);

    const setCurrentSourceEnabled = useCallback((enabled) => {
        const nextEnabled = enabled === true;
        const currentSourceId = source?.sourceId || '';

        setRealtimeEnabled(nextEnabled);

        /*
         * Старый publisher может всё ещё хранить локальное enabled-состояние.
         * Вызываем callback как compatibility bridge, но глобальный state уже
         * изменён независимо от него.
         */
        const action = currentSourceId
            ? sourceActionsRef.current.get(currentSourceId)
            : null;
        action?.onEnabledChange?.(nextEnabled);
    }, [setRealtimeEnabled, source?.sourceId]);

    const toggleCurrentSourceEnabled = useCallback(() => {
        setCurrentSourceEnabled(realtimeEnabled === false);
    }, [realtimeEnabled, setCurrentSourceEnabled]);

    const openPanel = useCallback(() => setIsOpen(true), []);
    const closePanel = useCallback(() => setIsOpen(false), []);
    const togglePanel = useCallback(() => setIsOpen((previous) => !previous), []);

    const value = useMemo(() => ({
        source,
        isOpen,
        realtimeEnabled,
        setRealtimeEnabled,
        uiBridgeStatus,
        publishSource,
        clearSource,
        setCurrentSourceEnabled,
        toggleCurrentSourceEnabled,
        openPanel,
        closePanel,
        togglePanel,
    }), [
        source,
        isOpen,
        realtimeEnabled,
        setRealtimeEnabled,
        uiBridgeStatus,
        publishSource,
        clearSource,
        setCurrentSourceEnabled,
        toggleCurrentSourceEnabled,
        openPanel,
        closePanel,
        togglePanel,
    ]);

    return (
        <AdminRealtimePanelContext.Provider value={value}>
            {children}
        </AdminRealtimePanelContext.Provider>
    );
};

export const useAdminRealtimePanel = () => {
    const context = useContext(AdminRealtimePanelContext);

    if (!context) {
        throw new Error(
            'useAdminRealtimePanel must be used inside AdminRealtimePanelProvider'
        );
    }

    return context;
};

export const useAdminRealtimePanelPublisher = (sourceId, payload) => {
    const { publishSource, clearSource } = useAdminRealtimePanel();

    useEffect(() => {
        publishSource(sourceId, payload);
    }, [payload, publishSource, sourceId]);

    useEffect(() => () => {
        clearSource(sourceId);
    }, [clearSource, sourceId]);
};

export default AdminRealtimePanelProvider;
